import React, { useState, useRef, useCallback, useContext } from "react"
import {
  HiX,
  HiOutlineDocumentText,
  HiOutlineUpload,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineRefresh,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
} from "react-icons/hi"
import AuthContext from "store/context/PublicContext"
import { post_middleware_series_bulk_csv } from "services/middleware_services"

interface CsvBulkUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

type UploadStatus = "idle" | "dragging" | "uploading" | "success" | "error"

interface OsrmDataResult {
  found: any[]
  notFound: Array<{ serieSap: string; error?: string }>
}

interface BulkResult {
  summary: string
  total: number
  updated: number
  skipped: number
  ignoredByFilter?: number
  selectedCategories?: string[]
  skippedDetails?: Array<{ serie: string; category: string }>
  bulkResult?: { matchedCount: number; modifiedCount: number } | null
  successfulUpdates?: string[]
  notFound?: string[]
  osrmData?: OsrmDataResult
}

const ALL_CATEGORIES = [
  'Stock almacen usado-No es parte del alcance',
  'Venta no descargada',
  'Disponible en almacén OL',
  'Movimiento manual en EBS no se refleja',
  'Dejar serie en estado libre y almacén columna "C"',
  'Ingresar en estado libre con almacén columna "C"',
  'No se reconoce el sku',
  'Orden/ Fecha/ Nomb cliente',
]

const CAT_LABELS: Record<string, string> = {
  'Stock almacen usado-No es parte del alcance': '→ 6 - eliminado',
  'Venta no descargada': '→ 1 - vendido',
  'Disponible en almacén OL': '→ 0 - libre',
  'Movimiento manual en EBS no se refleja': '→ 6 - eliminado',
  'Dejar serie en estado libre y almacén columna "C"': '→ 0 - libre + bodega',
  'Ingresar en estado libre con almacén columna "C"': '→ 0 - libre + bodega',
  'No se reconoce el sku': '→ actualiza SKU',
  'Orden/ Fecha/ Nomb cliente': 'solo lectura',
}

const CsvBulkUpdateModal: React.FC<CsvBulkUpdateModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user: { token } } = useContext(AuthContext)

  const [status, setStatus] = useState<UploadStatus>("idle")
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkResult | null>(null)

  // Category selection — arranca con todas seleccionadas
  const [selectedCats, setSelectedCats] = useState<string[]>([...ALL_CATEGORIES])
  const [showCatSelector, setShowCatSelector] = useState(true)
  const [showSkipped, setShowSkipped] = useState(false)
  const [showOrdenFecha, setShowOrdenFecha] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStatus("idle")
    setSelectedFile(null)
    setError(null)
    setResult(null)
    setShowSkipped(false)
    setShowOrdenFecha(false)
  }

  const handleClose = () => { reset(); onClose() }

  const validateFile = (file: File): string | null => {
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls"))
      return "Solo se aceptan archivos .xlsx / .xls"
    if (file.size > 50 * 1024 * 1024) return "El archivo no debe superar los 50 MB"
    return null
  }

  const handleFile = useCallback((file: File) => {
    const err = validateFile(file)
    if (err) { setError(err); setStatus("error"); return }
    setError(null); setResult(null); setSelectedFile(file); setStatus("idle")
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
    e.target.value = ""
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setStatus("idle")
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); setStatus("dragging") }
  const handleDragLeave = () => setStatus("idle")

  const toggleCat = (cat: string) => {
    setSelectedCats(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    )
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    if (selectedCats.length === 0) {
      setError("Selecciona al menos una categoría.")
      return
    }
    setStatus("uploading"); setError(null); setResult(null)
    try {
      // Si están todas seleccionadas enviamos ALL, sino el array
      const cats = selectedCats.length === ALL_CATEGORIES.length ? "ALL" : selectedCats
      const res = await post_middleware_series_bulk_csv(selectedFile, token, cats as any)
      const msg = res?.data?.message as BulkResult
      setResult(msg)
      setStatus("success")
      if (msg?.updated > 0 && onSuccess) onSuccess()
    } catch (err: any) {
      const raw = err?.response?.data?.message
      const msg = (typeof raw === "string" ? raw : raw?.message || raw?.detail) ||
        err?.message || "Error al procesar el archivo Excel."
      setError(typeof msg === "string" ? msg : JSON.stringify(msg))
      setStatus("error")
    }
  }

  if (!isOpen) return null

  const dropAreaClass = [
    "relative flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border-2 border-dashed transition-all cursor-pointer select-none",
    status === "dragging"
      ? "border-teal-400 bg-teal-50/60 scale-[1.01]"
      : selectedFile && status !== "error"
        ? "border-emerald-300 bg-emerald-50/40"
        : "border-gray-200 bg-[#F2F2F7]/60 hover:border-teal-300 hover:bg-teal-50/30",
  ].join(" ")

  const bulkObj = result?.bulkResult && !Array.isArray(result.bulkResult)
    ? result.bulkResult as { matchedCount: number; modifiedCount: number }
    : null

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 sm:p-0">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative z-10 w-full sm:max-w-lg flex flex-col bg-white
          rounded-3xl sm:rounded-2xl shadow-2xl max-h-[92dvh] sm:max-h-[92vh]
          animate-csv-bulk-up overflow-hidden mb-2 sm:mb-0">

        {/* Drag handle mobile */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 pt-4 sm:pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <HiOutlineDocumentText className="text-teal-500 text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-black text-gray-900 tracking-tight">Carga masiva por Excel</h2>
              <p className="text-[11px] text-gray-400 font-medium">Actualización en masa · hojas OMNIX_ / EBS_</p>
            </div>
          </div>
          <button onClick={handleClose}
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors">
            <HiX className="text-sm" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 flex flex-col gap-4">

          {/* Drop zone */}
          {status !== "success" && (
            <div className={dropAreaClass} onDrop={handleDrop} onDragOver={handleDragOver}
              onDragLeave={handleDragLeave} onClick={() => inputRef.current?.click()}>
              <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleInputChange} />
              {selectedFile ? (
                <>
                  <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
                    <HiOutlineDocumentText className="text-emerald-500 text-2xl" />
                  </div>
                  <div className="text-center">
                    <p className="text-[13px] font-bold text-gray-800 truncate max-w-[260px]">{selectedFile.name}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {(selectedFile.size / 1024).toFixed(1)} KB · XLSX
                    </p>
                  </div>
                  <span className="text-[11px] text-teal-500 font-semibold">Clic para cambiar archivo</span>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center">
                    <HiOutlineUpload className="text-gray-400 text-2xl" />
                  </div>
                  <div className="text-center">
                    <p className="text-[13px] font-semibold text-gray-700">Arrastra tu archivo Excel aquí</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">o haz clic para seleccionar</p>
                  </div>
                  <span className="text-[10px] text-gray-300 font-bold uppercase tracking-widest">
                    Max 50 MB · .xlsx / .xls
                  </span>
                </>
              )}
            </div>
          )}

          {/* Category selector */}
          {status !== "success" && (
            <div className="flex flex-col gap-0 bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden">
              {/* Header toggle */}
              <button
                onClick={() => setShowCatSelector(v => !v)}
                className="flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100/60 transition-colors"
              >
                <div>
                  <p className="text-[12px] font-black text-gray-700">
                    Categorías
                    <span className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                      selectedCats.length === ALL_CATEGORIES.length
                        ? 'bg-teal-100 text-teal-700'
                        : selectedCats.length === 0
                          ? 'bg-rose-100 text-rose-600'
                          : 'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedCats.length === ALL_CATEGORIES.length
                        ? 'Todas'
                        : `${selectedCats.length} / ${ALL_CATEGORIES.length}`}
                    </span>
                  </p>
                  {!showCatSelector && selectedCats.length < ALL_CATEGORIES.length && selectedCats.length > 0 && (
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5 truncate max-w-[340px]">
                      {selectedCats.slice(0, 2).join(", ")}{selectedCats.length > 2 ? ` +${selectedCats.length - 2} más` : ""}
                    </p>
                  )}
                </div>
                {showCatSelector
                  ? <HiOutlineChevronUp className="text-gray-400 text-base flex-shrink-0" />
                  : <HiOutlineChevronDown className="text-gray-400 text-base flex-shrink-0" />}
              </button>

              {showCatSelector && (
                <div className="border-t border-gray-100 flex flex-col max-h-64 overflow-y-auto overscroll-contain">
                  {/* Select / Deselect all buttons */}
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-white sticky top-0 z-10">
                    <button
                      onClick={() => setSelectedCats([...ALL_CATEGORIES])}
                      className="flex-1 py-1.5 text-[11px] font-bold rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                    >
                      Seleccionar todas
                    </button>
                    <button
                      onClick={() => setSelectedCats([])}
                      className="flex-1 py-1.5 text-[11px] font-bold rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                    >
                      Deseleccionar todas
                    </button>
                  </div>

                  {/* Category checkboxes */}
                  <div className="flex flex-col gap-0 divide-y divide-gray-50 px-2 py-1">
                    {ALL_CATEGORIES.map(cat => (
                      <label key={cat} className="flex items-start gap-3 cursor-pointer px-2 py-2.5 rounded-lg hover:bg-gray-100/60 transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedCats.includes(cat)}
                          onChange={() => toggleCat(cat)}
                          className="w-4 h-4 accent-teal-600 rounded mt-0.5 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="text-[11px] font-semibold text-gray-700 leading-snug block">{cat}</span>
                          <span className="text-[10px] text-gray-400">{CAT_LABELS[cat]}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
              <HiOutlineExclamationCircle className="text-rose-500 text-lg flex-shrink-0 mt-0.5" />
              <p className="text-[12px] font-medium text-rose-700 leading-relaxed">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && status === "success" && (
            <div className="flex flex-col gap-3">

              {/* Summary cards */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "Total", value: result.total, color: "text-gray-800", bg: "bg-gray-50 border-gray-100" },
                  { label: "Actualizadas", value: result.updated, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
                  { label: "Omitidas", value: result.skipped, color: "text-amber-600", bg: "bg-amber-50 border-amber-100" },
                  { label: "Ignoradas", value: result.ignoredByFilter ?? 0, color: "text-gray-400", bg: "bg-gray-50 border-gray-200" },
                ].map(({ label, value, color, bg }) => (
                  <div key={label} className={`flex flex-col items-center border rounded-2xl py-3 px-1 ${bg}`}>
                    <span className={`text-[20px] font-black ${color}`}>{value}</span>
                    <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 text-center leading-tight">{label}</span>
                  </div>
                ))}
              </div>

              {/* Summary text */}
              <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                <HiOutlineCheckCircle className="text-emerald-500 text-lg flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-emerald-800">{result.summary}</p>
                  {bulkObj && (
                    <p className="text-[10px] text-emerald-600 mt-0.5">
                      Matched: {bulkObj.matchedCount} · Modified: {bulkObj.modifiedCount}
                    </p>
                  )}
                </div>
              </div>

              {/* Not Found en BD (Actualización) */}
              {result.notFound && result.notFound.length > 0 && (
                <div className="flex items-center gap-2.5 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mt-1">
                  <HiOutlineExclamationCircle className="text-amber-500 text-lg flex-shrink-0" />
                  <p className="text-[11px] font-medium text-amber-700">
                    <span className="font-bold">{result.notFound.length}</span> series omitidas porque no existen en BD
                  </p>
                </div>
              )}

              {/* Selected categories */}
              {result.selectedCategories && result.selectedCategories.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {result.selectedCategories.map(c => (
                    <span key={c} className="text-[10px] bg-teal-50 border border-teal-100 text-teal-700 font-semibold px-2 py-0.5 rounded-full truncate max-w-[200px]">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {/* Orden Fecha Cliente (osrmData) */}
              {result.osrmData && (() => {
                const ofc = result.osrmData as OsrmDataResult
                const found = ofc.found ?? []
                const notFound = ofc.notFound ?? []
                const total = found.length + notFound.length
                if (total === 0) return null

                return (
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setShowOrdenFecha(v => !v)}
                      className="flex items-center justify-between w-full px-3 py-2.5 bg-indigo-50 border border-indigo-100 rounded-xl text-left"
                    >
                      <div>
                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                          Orden / Fecha / Nomb cliente
                        </p>
                        <p className="text-[10px] text-indigo-400 mt-0.5">
                          <span className="text-emerald-600 font-bold">{found.length} encontradas</span>
                          {' · '}
                          <span className="text-rose-500 font-bold">{notFound.length} no encontradas</span>
                          {' · '}{total} consultadas
                        </p>
                      </div>
                      {showOrdenFecha
                        ? <HiOutlineChevronUp className="text-indigo-400 text-sm flex-shrink-0" />
                        : <HiOutlineChevronDown className="text-indigo-400 text-sm flex-shrink-0" />}
                    </button>

                    {showOrdenFecha && (
                      <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50 max-h-64 overflow-y-auto">
                        {/* Found */}
                        {found.map((rec: any) => (
                          <div key={rec.serieSap} className="flex items-start gap-3 px-3 py-2.5 bg-white hover:bg-gray-50 transition-colors">
                            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-mono font-bold text-gray-800 truncate">{rec.serieSap}</p>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1">
                                <p className="text-[10px] text-gray-500 truncate"><span className="text-gray-400">Orden:</span> {rec.ordenId}</p>
                                <p className="text-[10px] text-gray-500 truncate"><span className="text-gray-400">Cliente:</span> {rec.cliente}</p>
                                <p className="text-[10px] text-gray-500 truncate"><span className="text-gray-400">Estado:</span> {rec.estado}</p>
                                <p className="text-[10px] text-gray-500 truncate"><span className="text-gray-400">Fecha:</span> {rec.fecha ? new Date(rec.fecha).toLocaleDateString() : 'N/A'}</p>
                              </div>
                            </div>
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 bg-emerald-50 text-emerald-600 border border-emerald-100">
                              OK
                            </span>
                          </div>
                        ))}
                        {/* Not found */}
                        {notFound.map((item) => (
                          <div key={item.serieSap} className="flex items-start gap-3 px-3 py-2.5 bg-white">
                            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0 bg-rose-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-mono font-bold text-gray-800 truncate">{item.serieSap}</p>
                              <p className="text-[10px] text-rose-400 font-medium">Sin orden / No existe</p>
                            </div>
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 bg-rose-50 text-rose-500 border border-rose-100">
                              404
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}

              {/* Skipped details */}
              {result.skippedDetails && result.skippedDetails.length > 0 && (
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setShowSkipped(v => !v)}
                    className="flex items-center justify-between w-full px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl text-left"
                  >
                    <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest">
                      Omitidas ({result.skippedDetails.length})
                    </p>
                    {showSkipped
                      ? <HiOutlineChevronUp className="text-amber-400 text-sm" />
                      : <HiOutlineChevronDown className="text-amber-400 text-sm" />}
                  </button>
                  {showSkipped && (
                    <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50 max-h-32 overflow-y-auto">
                      {result.skippedDetails.map((item, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 bg-white">
                          <span className="text-[11px] font-mono text-gray-700 truncate max-w-[180px]">{item.serie}</span>
                          <span className="text-[10px] text-amber-500 font-semibold truncate max-w-[140px] text-right">{item.category}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center gap-3 px-5 pt-3 pb-5 border-t border-gray-100 bg-white">
          {status === "success" ? (
            <>
              <button onClick={handleClose}
                className="flex-1 py-3.5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-bold hover:bg-gray-200 transition-colors">
                Cerrar
              </button>
              <button onClick={reset}
                className="flex-1 py-3.5 rounded-xl bg-teal-600 text-white text-[13px] font-bold
                  hover:bg-teal-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2">
                <HiOutlineRefresh className="text-base" />
                Nueva carga
              </button>
            </>
          ) : (
            <>
              <button onClick={handleClose} disabled={status === "uploading"}
                className="flex-1 py-3.5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-bold hover:bg-gray-200 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={handleUpload}
                disabled={!selectedFile || status === "uploading" || status === "error"}
                className="flex-1 py-3.5 rounded-xl bg-teal-600 text-white text-[13px] font-bold
                  hover:bg-teal-700 active:scale-[0.98] transition-all
                  disabled:opacity-50 flex items-center justify-center gap-2">
                {status === "uploading" ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <HiOutlineUpload className="text-base" />
                    Aplicar actualización
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes csvBulkUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-csv-bulk-up {
          animation: csvBulkUp 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  )
}

export default CsvBulkUpdateModal
